import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, 
    FaLandmark, FaMoneyBillWave, FaBuilding, FaChartLine, FaExclamationCircle 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './GerenciadorPatrimonial.css';

const ItemModal = ({ isOpen, onClose, onSave, item, tipoAtivo }) => {
    // Estado inicial
    const [formData, setFormData] = useState({ 
        nome: '', 
        valor: '', 
        tipo: 'ativo_circulante',
        origem_recurso: 'capital_social' // Padrão seguro
    });

    useEffect(() => {
        if (isOpen) {
            if (item) {
                // Edição (simplificada, sem lógica de origem complexa para não quebrar legados)
                setFormData({ ...item, origem_recurso: 'ignore' });
            } else {
                // Criação: Define padrão inteligente
                let defaultTipo = 'ativo_circulante';
                if (tipoAtivo === 'passivos') defaultTipo = 'passivo_circulante';
                if (tipoAtivo === 'patrimonio') defaultTipo = 'patrimonio_liquido';
                
                setFormData({ 
                    nome: '', 
                    valor: '', 
                    tipo: defaultTipo, 
                    origem_recurso: 'capital_social' 
                });
            }
        }
    }, [item, isOpen, tipoAtivo]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    // Lógica para mostrar o campo de Origem apenas na criação de ATIVOS
    // (Pois Passivos já são a origem, e PL já é a origem)
    const showOrigemSelector = !item && formData.tipo.startsWith('ativo');

    return (
        <div className="gp-modal-overlay">
            <div className="gp-modal">
                <div className="gp-modal-header">
                    <h3>{item ? 'Editar Conta' : 'Novo Lançamento Contábil'}</h3>
                    <button type="button" onClick={onClose}><FaTimes /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="gp-form-group">
                        <label>Descrição da Conta</label>
                        <input 
                            type="text" 
                            required 
                            value={formData.nome}
                            onChange={e => setFormData({...formData, nome: e.target.value})}
                            placeholder="Ex: Reserva Técnica, Terreno SJC..."
                        />
                    </div>
                    
                    <div className="gp-row">
                        <div className="gp-form-group">
                            <label>Valor (R$)</label>
                            <input 
                                type="number" 
                                step="0.01" 
                                required 
                                value={formData.valor}
                                onChange={e => setFormData({...formData, valor: e.target.value})}
                                placeholder="0.00"
                            />
                        </div>
                        <div className="gp-form-group">
                            <label>Classificação</label>
                            <select 
                                value={formData.tipo} 
                                onChange={e => setFormData({...formData, tipo: e.target.value})}
                            >
                                <optgroup label="Ativos (Bens/Direitos)">
                                    <option value="ativo_circulante">Ativo Circulante (Curto Prazo)</option>
                                    <option value="ativo_nao_circulante">Ativo Não Circulante (Longo Prazo)</option>
                                </optgroup>
                                <optgroup label="Passivos (Dívidas)">
                                    <option value="passivo_circulante">Passivo Circulante</option>
                                    <option value="passivo_nao_circulante">Passivo Não Circulante</option>
                                </optgroup>
                                <optgroup label="Patrimônio">
                                    <option value="patrimonio_liquido">Patrimônio Líquido</option>
                                </optgroup>
                            </select>
                        </div>
                    </div>

                    {/* --- AQUI ESTÁ A BLINDAGEM --- */}
                    {showOrigemSelector && (
                        <div className="gp-blindagem-box">
                            <label><FaExclamationCircle /> Origem dos Recursos (Contrapartida)</label>
                            <p className="gp-hint">Para manter o balanço equilibrado, informe de onde veio o dinheiro para este Ativo.</p>
                            <select 
                                value={formData.origem_recurso} 
                                onChange={e => setFormData({...formData, origem_recurso: e.target.value})}
                                required
                            >
                                <option value="capital_social">Sócios / Capital Social (Cria PL Automático)</option>
                                <option value="lucros">Lucros Acumulados (Cria PL Automático)</option>
                                <option value="emprestimo">Empréstimo Bancário (Cria Passivo Automático)</option>
                                <option value="divergente">⚠️ Não Justificado / Outros (Gera Divergência)</option>
                            </select>
                        </div>
                    )}

                    <div className="gp-modal-footer">
                        <button type="button" onClick={onClose} className="gp-btn ghost">Cancelar</button>
                        <button type="submit" className="gp-btn primary"><FaSave /> Salvar Lançamento</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function GerenciadorPatrimonial() {
    const [activeTab, setActiveTab] = useState('ativos');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itens, setItens] = useState([]);
    const [loading, setLoading] = useState(true);

    const carregarItens = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/financeiro/patrimonio-manual');
            setItens(data);
        } catch (error) {
            toast.error("Erro ao carregar dados.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { carregarItens(); }, []);

    const handleSave = async (formData) => {
        try {
            if (editingItem) {
                // Edição não mexe na contrapartida complexa para evitar inconsistência na edição
                await api.put(`/api/financeiro/patrimonio-manual/${editingItem.id}`, formData);
                toast.success("Atualizado!");
            } else {
                await api.post('/api/financeiro/patrimonio-manual', formData);
                if (formData.origem_recurso && formData.origem_recurso !== 'divergente' && formData.tipo.startsWith('ativo')) {
                    toast.success("Lançamento Duplo Criado com Sucesso! (Ativo + Contrapartida)");
                } else {
                    toast.warn("Lançamento criado. Verifique o Balanço.");
                }
            }
            carregarItens();
            setIsModalOpen(false);
            setEditingItem(null);
        } catch (error) {
            toast.error("Erro ao salvar.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Deseja remover este registro? Se foi criado automaticamente, lembre-se de remover a contrapartida também.")) {
            try {
                await api.delete(`/api/financeiro/patrimonio-manual/${id}`);
                toast.success("Removido.");
                carregarItens();
            } catch (error) {
                toast.error("Erro ao remover.");
            }
        }
    };

    const openModal = (item = null) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const filteredItens = itens.filter(i => {
        if (activeTab === 'ativos') return i.tipo.startsWith('ativo');
        if (activeTab === 'passivos') return i.tipo.startsWith('passivo');
        return i.tipo === 'patrimonio_liquido';
    });

    const formatCurrency = (val) => Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const totalTab = filteredItens.reduce((acc, curr) => acc + parseFloat(curr.valor), 0);

    return (
        <div className="gp-container">
            <header className="gp-header">
                <div className="gp-title">
                    <div className="gp-icon-wrapper"><FaLandmark /></div>
                    <div>
                        <h1>Gerenciador Patrimonial Blindado</h1>
                        <p>Contabilidade de dupla entrada: Toda origem tem uma aplicação.</p>
                    </div>
                </div>
                <div className="gp-actions">
                    <button className="gp-btn primary" onClick={() => openModal()}>
                        <FaPlus /> Novo Lançamento
                    </button>
                </div>
            </header>

            <div className="gp-tabs">
                <button className={`gp-tab ${activeTab === 'ativos' ? 'active' : ''}`} onClick={() => setActiveTab('ativos')}>
                    <FaBuilding /> Ativos
                </button>
                <button className={`gp-tab ${activeTab === 'passivos' ? 'active' : ''}`} onClick={() => setActiveTab('passivos')}>
                    <FaMoneyBillWave /> Passivos
                </button>
                <button className={`gp-tab ${activeTab === 'patrimonio' ? 'active' : ''}`} onClick={() => setActiveTab('patrimonio')}>
                    <FaChartLine /> Patrimônio Líquido
                </button>
            </div>

            <div className="gp-content">
                <div className={`gp-summary-card ${activeTab}`}>
                    <div className="summary-info">
                        <span>Total nesta visão</span>
                        <h2>{formatCurrency(totalTab)}</h2>
                    </div>
                    <div className="summary-icon">
                        {activeTab === 'ativos' ? <FaBuilding /> : activeTab === 'passivos' ? <FaMoneyBillWave /> : <FaChartLine />}
                    </div>
                </div>

                <div className="gp-list-container">
                    <table className="gp-table">
                        <thead>
                            <tr>
                                <th>Conta</th>
                                <th>Classificação</th>
                                <th className="text-right">Valor</th>
                                <th className="text-center">Status</th>
                                <th className="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItens.length === 0 ? (
                                <tr><td colSpan="5" className="empty-state">Nenhum lançamento manual.</td></tr>
                            ) : (
                                filteredItens.map(item => (
                                    <tr key={item.id} className={item.divergente ? 'row-divergente' : ''}>
                                        <td>
                                            <strong>{item.nome}</strong>
                                            {item.divergente ? <small style={{display:'block', color: 'red'}}>Sem origem definida</small> : null}
                                        </td>
                                        <td>
                                            <span className={`badge ${item.tipo.includes('nao') ? 'nao_circulante' : item.tipo.includes('patrimonio') ? 'patrimonio' : 'circulante'}`}>
                                                {item.tipo.replace(/_/g, ' ').toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-right valor">{formatCurrency(item.valor)}</td>
                                        <td className="text-center">
                                            {item.divergente ? 
                                                <span className="tag-alerta">DIVERGENTE</span> : 
                                                <span className="tag-ok">OK</span>
                                            }
                                        </td>
                                        <td className="text-center actions">
                                            <button onClick={() => openModal(item)}><FaEdit /></button>
                                            <button onClick={() => handleDelete(item.id)} className="delete"><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ItemModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} item={editingItem} tipoAtivo={activeTab} />
        </div>
    );
}